import { ReactNode } from 'react';
import { Panel } from 'primereact/panel';
import { formatarCNPJ, formatarCPF } from '../../../../utils';

// Helpers de formatação e layout compartilhados pelas respostas Serasa (Consulta AI)

export const formatarMoeda = (valor?: number | null) =>
  valor == null
    ? '—'
    : Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatarCentavos = (valor?: number | null) =>
  valor == null ? '—' : formatarMoeda(Number(valor) / 100);

export const formatarData = (valor?: string | null) => {
  if (!valor) return '—';
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?(?:T(\d{2}):(\d{2}))?/.exec(valor);
  if (!match) return valor;
  const [, ano, mes, dia, hora, minuto] = match;
  if (!dia) return `${mes}/${ano}`;
  return hora ? `${dia}/${mes}/${ano} ${hora}:${minuto}` : `${dia}/${mes}/${ano}`;
};

export const formatarDocumento = (valor?: string | null) => {
  if (!valor) return '—';
  const digitos = String(valor).replace(/\D/g, '');
  if (digitos.length === 11) return formatarCPF(digitos);
  if (digitos.length === 14) return formatarCNPJ(digitos);
  return valor;
};

export const formatarTexto = (valor?: string | null) => {
  if (!valor) return '—';
  if (!valor.includes('_')) return valor;
  const texto = valor.replace(/_/g, ' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const ROTULOS: Record<string, string> = {
  nome: 'Nome',
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  documento: 'Documento',
  cpf: 'CPF',
  cnpj: 'CNPJ',
  situacao: 'Situação',
  situacao_cadastral: 'Situação cadastral',
  data_nascimento: 'Data de nascimento',
  nascimento: 'Data de nascimento',
  nome_mae: 'Nome da mãe',
  sexo: 'Sexo',
  estado_civil: 'Estado civil',
  escolaridade: 'Escolaridade',
  fundacao: 'Fundação',
  data_abertura: 'Data de abertura',
  idade_empresa: 'Idade da empresa',
  credor: 'Credor',
  data: 'Data',
  segmento: 'Segmento',
  quantidade: 'Quantidade',
  natureza: 'Natureza',
  natureza_operacao: 'Natureza',
  participacao_pct: 'Participação',
  participacao: 'Participação',
  uf: 'UF',
  cidade: 'Cidade',
  desde: 'Desde',
  atualizado_em: 'Atualizado em',
  cargo: 'Cargo',
  mes: 'Mês',
  bancos: 'Bancos',
  empresas: 'Empresas',
};

export const rotulo = (chave: string) => {
  if (ROTULOS[chave]) return ROTULOS[chave];
  const texto = chave
    .replace(/_cents$/, '')
    .replace(/_pct$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// Formata um valor primitivo de acordo com o nome do campo
export const formatarValor = (chave: string, valor: any): string => {
  if (valor == null || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (chave.endsWith('_cents')) return formatarCentavos(Number(valor));
  if (chave.endsWith('_pct') || chave === 'participacao') return `${valor}%`;
  if (typeof valor === 'number') return valor.toLocaleString('pt-BR');
  const texto = String(valor);
  if (/^\d{4}-\d{2}(-\d{2})?(T.*)?$/.test(texto)) return formatarData(texto);
  if (/(documento|cpf|cnpj)/i.test(chave)) return formatarDocumento(texto);
  if (chave === 'situacao' || chave === 'tipo') return formatarTexto(texto);
  return texto;
};

export const isPrimitivo = (valor: any) => valor == null || typeof valor !== 'object';

export const CORES = {
  azul: '#1E3A8A',
  azulClaro: '#93C5FD',
  cinza: '#9CA3AF',
  texto: '#1F2937',
  borda: '#E5E7EB',
  vermelho: '#DC2626',
  vermelhoFundo: '#FEF2F2',
  verde: '#16A34A',
  verdeFundo: '#F0FDF4',
  amarelo: '#D97706',
  amareloFundo: '#FFFBEB',
};

export function Container({
  titulo,
  isConsultaNova,
  alerta,
  badge,
  children,
}: {
  titulo: string;
  isConsultaNova?: boolean;
  alerta?: boolean;
  badge?: ReactNode;
  children: ReactNode;
}) {
  if (!isConsultaNova) {
    return <Panel header={titulo}>{children}</Panel>;
  }
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        border: `1px solid ${CORES.borda}`,
        boxShadow: '0 4px 20px -2px rgba(0,0,0,0.05)',
        padding: '24px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              backgroundColor: alerta ? CORES.vermelhoFundo : CORES.verdeFundo,
              borderRadius: '10px',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i
              className={alerta ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'}
              style={{ color: alerta ? CORES.vermelho : CORES.verde, fontSize: '18px' }}
            />
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: CORES.azul }}>{titulo}</h3>
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

export function Badge({ texto, tom }: { texto: string; tom: 'vermelho' | 'verde' | 'amarelo' }) {
  const cor = tom === 'vermelho' ? CORES.vermelho : tom === 'verde' ? CORES.verde : CORES.amarelo;
  const fundo = tom === 'vermelho' ? CORES.vermelhoFundo : tom === 'verde' ? CORES.verdeFundo : CORES.amareloFundo;
  return (
    <span
      style={{
        backgroundColor: fundo,
        color: cor,
        border: `1px solid ${cor}33`,
        borderRadius: '20px',
        padding: '4px 14px',
        fontSize: '0.75rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {texto}
    </span>
  );
}

export function Secao({ titulo, children, extra }: { titulo: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <div style={{ marginTop: '24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          flexWrap: 'wrap',
          borderBottom: `1px solid ${CORES.borda}`,
          paddingBottom: '6px',
          marginBottom: '12px',
        }}
      >
        <h4 style={{ fontSize: '0.85rem', fontWeight: 700, margin: 0, color: CORES.azul, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
          {titulo}
        </h4>
        {extra}
      </div>
      {children}
    </div>
  );
}

export function Campos({ campos }: { campos: { label: string; value?: ReactNode }[] }) {
  const visiveis = campos.filter(c => c.value != null && c.value !== '' && c.value !== '—');
  if (!visiveis.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
      {visiveis.map(c => (
        <div key={c.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: CORES.cinza, textTransform: 'uppercase' }}>{c.label}</span>
          <span style={{ fontSize: 'small', color: CORES.texto, fontWeight: 500, wordBreak: 'break-word' }}>{c.value}</span>
        </div>
      ))}
    </div>
  );
}

export function CamposObjeto({ objeto, ignorar = [] }: { objeto: any; ignorar?: string[] }) {
  if (!objeto || typeof objeto !== 'object') return null;
  const campos = Object.entries(objeto)
    .filter(([chave, valor]) => isPrimitivo(valor) && !ignorar.includes(chave))
    .map(([chave, valor]) => ({ label: rotulo(chave), value: formatarValor(chave, valor) }));
  return <Campos campos={campos} />;
}

export function Resumos({ itens }: { itens: { label: string; value: ReactNode; icon: string }[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
      {itens.map(item => (
        <div
          key={item.label}
          style={{
            backgroundColor: CORES.azul,
            borderRadius: '12px',
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ color: CORES.azulClaro, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>{item.label}</span>
            <span style={{ color: '#FFFFFF', fontWeight: 800, fontSize: '1rem' }}>{item.value}</span>
          </div>
          <span
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className={item.icon} style={{ color: '#FFFFFF', fontSize: '14px' }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export type Coluna = { chave: string; titulo: string; render?: (linha: any) => ReactNode; destaque?: boolean };

export function Tabela({ linhas, colunas }: { linhas: any[]; colunas?: Coluna[] }) {
  if (!linhas?.length) return null;
  // Sem colunas definidas, usa a união dos campos primitivos das linhas
  const cols: Coluna[] =
    colunas ??
    Array.from(
      new Set(linhas.flatMap(l => Object.keys(l ?? {}).filter(k => isPrimitivo(l[k]))))
    ).map(chave => ({ chave, titulo: rotulo(chave) }));
  return (
    <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #F3F4F6' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'small' }}>
        <thead>
          <tr style={{ backgroundColor: '#F9FAFB', borderBottom: `2px solid ${CORES.borda}` }}>
            {cols.map(c => (
              <th
                key={c.chave}
                style={{
                  padding: '10px 14px',
                  fontWeight: 700,
                  color: '#6B7280',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, idx) => (
            <tr key={idx} style={{ borderBottom: idx < linhas.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              {cols.map(c => (
                <td
                  key={c.chave}
                  style={{
                    padding: '10px 14px',
                    color: c.destaque ? CORES.vermelho : '#374151',
                    fontWeight: c.destaque ? 700 : 400,
                    whiteSpace: c.destaque ? 'nowrap' : undefined,
                  }}
                >
                  {c.render ? c.render(linha) : formatarValor(c.chave, linha?.[c.chave])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Mensagem({ texto }: { texto?: string | null }) {
  if (!texto) return null;
  return (
    <div
      style={{
        backgroundColor: '#F9FAFB',
        border: `1px solid ${CORES.borda}`,
        borderRadius: '10px',
        padding: '10px 14px',
        fontSize: 'small',
        color: '#4B5563',
        marginBottom: '12px',
      }}
    >
      <i className="pi pi-info-circle" style={{ marginRight: '8px', color: CORES.cinza }} />
      {texto}
    </div>
  );
}

const corScore = (valor: number, maximo: number) => {
  const proporcao = maximo ? valor / maximo : 0;
  if (proporcao < 0.3) return CORES.vermelho;
  if (proporcao < 0.7) return CORES.amarelo;
  return CORES.verde;
};

export function Score({
  valor,
  minimo = 0,
  maximo = 1000,
  faixa,
  detalhes,
  mensagem,
}: {
  valor?: number | null;
  minimo?: number;
  maximo?: number;
  faixa?: string;
  detalhes?: { label: string; value?: ReactNode }[];
  mensagem?: string | null;
}) {
  if (valor == null) {
    return <Mensagem texto={mensagem || 'Score não calculado.'} />;
  }
  const cor = corScore(valor - minimo, maximo - minimo);
  const posicao = Math.max(0, Math.min(100, ((valor - minimo) / ((maximo - minimo) || 1)) * 100));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', alignItems: 'center' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <span style={{ fontSize: '2.25rem', fontWeight: 800, color: cor, lineHeight: 1 }}>{valor}</span>
          <span style={{ fontSize: 'small', color: CORES.cinza }}>de {maximo}</span>
        </div>
        {faixa && <div style={{ marginTop: '6px', fontWeight: 700, color: cor, fontSize: 'small' }}>{faixa}</div>}
        <div
          style={{
            position: 'relative',
            height: '8px',
            borderRadius: '4px',
            marginTop: '12px',
            background: `linear-gradient(90deg, ${CORES.vermelho} 0%, ${CORES.amarelo} 50%, ${CORES.verde} 100%)`,
          }}
        >
          <span
            style={{
              position: 'absolute',
              left: `${posicao}%`,
              top: '-4px',
              width: '16px',
              height: '16px',
              marginLeft: '-8px',
              borderRadius: '50%',
              backgroundColor: '#FFFFFF',
              border: `3px solid ${cor}`,
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: CORES.cinza, marginTop: '4px' }}>
          <span>{minimo}</span>
          <span>{maximo}</span>
        </div>
      </div>
      {detalhes && <Campos campos={detalhes} />}
    </div>
  );
}

// Renderiza blocos desconhecidos (ex.: campos novos da API) sem precisar de layout específico
export function BlocoGenerico({ titulo, valor }: { titulo: string; valor: any }) {
  if (valor == null) return null;
  if (Array.isArray(valor)) {
    if (!valor.length) return null;
    if (valor.every(isPrimitivo)) {
      return (
        <Secao titulo={titulo}>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'small', color: '#374151' }}>
            {valor.map((v, i) => <li key={i}>{String(v)}</li>)}
          </ul>
        </Secao>
      );
    }
    return (
      <Secao titulo={titulo}>
        <Tabela linhas={valor} />
      </Secao>
    );
  }
  if (typeof valor !== 'object') {
    return (
      <Secao titulo={titulo}>
        <Campos campos={[{ label: titulo, value: formatarValor('', valor) }]} />
      </Secao>
    );
  }
  const filhos = Object.entries(valor).filter(([, v]) => !isPrimitivo(v));
  return (
    <Secao titulo={titulo}>
      <CamposObjeto objeto={valor} />
      {filhos.map(([chave, v]) => (
        <BlocoGenerico key={chave} titulo={rotulo(chave)} valor={v} />
      ))}
    </Secao>
  );
}
